import React, { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

// Helper to auto-format ImageKit URLs for WebP/AVIF compression & sizing
const getOptimizedUrl = (url, width, height, quality = 80) => {
  if (!url) return "";
  if (!url.includes("ik.imagekit.io")) return url;
  const baseUrl = url.split("?")[0];
  const sizeQuery = height ? `w-${width},h-${height}` : `w-${width}`;
  return `${baseUrl}?tr=${sizeQuery},q-${quality},f-auto`;
};

// Clean, performance-optimized client logos
const logos = [
  { name: "BAB", url: getOptimizedUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/BAB.PNG", 280, 136) },
  { name: "Knight Tech", url: getOptimizedUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/knight_tech.PNG", 280, 136) },
  { name: "Softbeck LLC", url: getOptimizedUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/Softbeck_llc.png", 280, 136) },
  { name: "The Blixify", url: getOptimizedUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/theblixify.png", 280, 136) },
  { name: "GoldenCoin", url: getOptimizedUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/goldencoin.PNG", 280, 136) },
  { name: "GreeksPod", url: getOptimizedUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/greekspod.png", 280, 136) },
  { name: "Consultancy", url: getOptimizedUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/Consultancy.jpg", 280, 136) },
];

const duplicatedLogos = [...logos, ...logos];

const HERO_IMAGE_URL = getOptimizedUrl("https://ik.imagekit.io/g9mnvfpan/hero-2.png", 1600);

const Hero = () => {
  const container = useRef(null);
  const marqueeRef = useRef(null);

  useGSAP(
    () => {
      requestAnimationFrame(() => {
        // 1. Entrance Reveal (Swipe effect)
        gsap.to(".anim-swipe", {
          yPercent: -100,
          duration: 1.5,
          ease: "power4.inOut",
        });

        // 2. Text Fade-Out on Scroll
        gsap.to(".hero__overlay", {
          opacity: 0,
          scrollTrigger: {
            trigger: ".hero",
            start: "top top",
            end: "bottom center",
            scrub: true,
          },
        });

        // 3. Image Zoom on Scroll
        gsap.to(".hero__image-cont img", {
          scale: 1.3,
          ease: "none",
          scrollTrigger: {
            trigger: ".hero",
            start: "top top",
            end: "+=3000",
            scrub: 1,
          },
        });

        // 4. Seamless Marquee Animation
        if (marqueeRef.current) {
          gsap.to(marqueeRef.current, {
            xPercent: -50,
            ease: "none",
            duration: 25,
            repeat: -1,
          });
        }
      });
    },
    { scope: container }
  );

  return (
    <section
      className="hero"
      ref={container}
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Content Overlay */}
      <div
        className="hero__overlay"
        style={{
          position: "relative",
          zIndex: 10,
          minHeight: "280px",
        }}
      >
       <span
          style={{
            color: "#fd8110",
            textTransform: "uppercase",
            fontSize: "13px",
            fontWeight: 700,
            letterSpacing: "0.15em",
            marginBottom: "12px",
            display: "block",
          }}
        >
          E-Commerce Excellence
        </span>

        <h1
          style={{
            color: "#ffffff",
            fontSize: "clamp(2.5rem, 5vw, 4.2rem)",
            fontWeight: 800,
            lineHeight: 1.15,
            marginBottom: "20px",
            letterSpacing: "-0.02em",
          }}
        >
          Softbeck Learning Hub
        </h1>

        <p
          style={{
            color: "rgba(255, 255, 255, 0.82)",
            fontSize: "clamp(1rem, 2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.6,
            maxWidth: "720px",
            marginBottom: "32px",
          }}
        >
          We provide end-to-end e-commerce solutions, specializing in professional account management, scaling, and creation services for <strong style={{ color: "#fff" }}>eBay</strong>, <strong style={{ color: "#fff" }}>TikTok Shop</strong>, and <strong style={{ color: "#fff" }}>Shopify</strong> platforms.
        </p>
        <br />
        <Link to="/contact" className="cta-button">
          Contact Us
        </Link>
      </div>

      {/* Single Background Image Container */}
      <div
        className="hero__inner"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        <div
          className="hero__image-cont"
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            willChange: "transform",
          }}
        >
          <img
            src={HERO_IMAGE_URL}
            alt="Softbeck learning background"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            fetchPriority="high"
            loading="eager"
            decoding="async"
          />
          <div className="anim-swipe" />
        </div>
      </div>

      {/* Color Overlay for Text Readability */}
      <div className="hero__color-overlay" />

      {/* Client Logos Marquee */}
      <div
        className="hero__marquee-wrapper"
        style={{
          position: "relative",
          zIndex: 10,
          overflow: "hidden",
          width: "100%",
          height: "80px",
        }}
      >
        <div
          className="hero__marquee-inner"
          ref={marqueeRef}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "40px",
            width: "max-content",
            willChange: "transform",
          }}
        >
          {duplicatedLogos.map((logo, index) => (
            <div
              className="marquee-item"
              key={index}
              style={{
                flex: "0 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "140px",
                height: "68px",
              }}
            >
              <img
                src={logo.url}
                alt={`${logo.name} logo`}
                width={140}
                height={68}
                loading="lazy"
                decoding="async"
                style={{
                  maxWidth: "140px",
                  maxHeight: "68px",
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  padding: "10px",
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;